import { useEffect, useMemo, useRef } from "react";
import type { BeatSyncDto } from "@repo/api/background-music";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import Toast from "@/lib/components/Toast";

/**
 * Returns whether the current clip durations are synced to any of the given
 * beat syncs. Shows a toast when the video transitions from synced to out of
 * sync (e.g. after editing clip durations).
 */
export function useIsSyncedToBeat(beatSyncs: BeatSyncDto[]): boolean {
  const compositionJson = useEditorStore(
    (state) => state.video?.compositionJson,
  );
  const areClipDurationsSyncedToBeatSync = useEditorStore(
    (state) => state.service.areClipDurationsSyncedToBeatSync,
  );

  // Recomputes whenever the composition (clip durations) or beat syncs change.
  const isSyncedToBeat = useMemo(
    () =>
      beatSyncs.some((beatSync) =>
        areClipDurationsSyncedToBeatSync(beatSync.cuttingPoints),
      ),
    [beatSyncs, compositionJson, areClipDurationsSyncedToBeatSync],
  );

  const wasSyncedToBeat = useRef(isSyncedToBeat);
  useEffect(() => {
    if (wasSyncedToBeat.current && !isSyncedToBeat && beatSyncs.length > 0) {
      Toast.info("Your video is no longer synced to the beat");
    }
    wasSyncedToBeat.current = isSyncedToBeat;
  }, [isSyncedToBeat, beatSyncs.length]);

  return isSyncedToBeat;
}
